import { DateTime } from 'luxon';

import { accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { selectIsAiBusinessToolsEnabled } from 'app-components/flux/ai-chat/aiChatSelectors';
import { businessSelectors } from 'app-components/flux/business';
import { getCategoriesById } from 'app-components/flux/categories/categoriesSelectors';
import { getId } from 'app-components/flux/core/resourceStoreTypes';
import { customersSelectors } from 'app-components/flux/customers';
import { getCurrentDateString } from 'app-components/flux/date/dateSelectors';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { projectsSelectors } from 'app-components/flux/projects';
import type { Project, ProjectStatus } from 'app-components/flux/projects/projectsTypes';
import {
  getInvoices,
  selectAccountsReceivableCredits,
  selectInvoiceChipStatuses,
  selectInvoiceDashboardSummary,
  selectInvoicesLastPaidDate,
  selectPaidAmountForInvoicesTypes,
  selectProductsAndServicesStatsLast90Days,
} from 'app-components/flux/transactions/invoicesSelectors';
import { getResourcesById } from 'app-components/flux/transactions/transactionsSelectors';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { isUnacceptedScheduledTxn } from 'app-components/flux/transactions/transactionsUtils';
import { getLogger } from 'app-components/utils/core';
import { ensureArray } from 'app-components/utils/core/immutableHelpers';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { safeParse } from '../../aiChatUtils';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolMessage } from '../llmToolsTypes';
import {
  getAgingArgsSchema,
  getBusinessTransactionsArgsSchema,
  getInvoicesArgsSchema,
  getProjectsArgsSchema,
  getTopProductsArgsSchema,
} from '../schemas/business';
import {
  buildArAccountIds,
  type CoaInfo,
  type CoaLookupMaps,
  getCoaInfoWithMaps,
  isEstimatedTransaction,
  isInvestmentTransaction,
} from '../utils';

const log = getLogger('llmBusinessTools');

const DEFAULT_MAX_ROWS = 50;
const UNPAID_EPSILON = 0.005;

type BusinessToolContext = { state: RootState; today: string };

const resolveContext = (): BusinessToolContext | { error: string } => {
  const state = store?.getState() as RootState | undefined;
  if (!state) {
    return { error: 'Account data is not loaded yet. Ask the user to try again in a moment.' };
  }
  if (!selectIsAiBusinessToolsEnabled(state)) {
    return { error: 'Business tools are not enabled for this account.' };
  }
  return { state, today: getCurrentDateString(state) };
};

const sumPaymentsReceivedSince = (state: RootState, since: string): number =>
  selectAccountsReceivableCredits(state).reduce((total, payment) => {
    if (payment.subtype === 'INVOICE_CREDIT_MEMO' || !payment.postedOn || payment.postedOn < since) return total;
    return ensureArray<{ txnId?: string; amount: number }>(payment.allocations ?? []).reduce(
      (received, alloc) =>
        alloc.txnId && Number.isFinite(alloc.amount) ? received + Math.abs(alloc.amount) : received,
      total,
    );
  }, 0);

const resolveBusinessId = (...candidates: Array<string | null | undefined>): string | null =>
  candidates.reduce<string | null>(
    (found, candidate) => found ?? accountsUtils.normalizeBusinessIdForUsage(candidate) ?? null,
    null,
  );

type MatchedProject = { project: Project; projectBusinessId: string | null };

const isContextError = (ctx: BusinessToolContext | { error: string }): ctx is { error: string } => 'error' in ctx;

const buildValidationError = (toolName: string, issues: Array<{ message: string }>): string =>
  `Invalid ${toolName} arguments: ${issues.map((issue) => issue.message).join('; ')}`;

const resolveMaxRows = (state: RootState, limit: number | undefined, totalCount: number): number => {
  const limitPayloads = !!featureFlagsSelectors.getFeatureFlag(state, 'aiChatLimitToolPayloads');
  const ceiling = limitPayloads ? DEFAULT_MAX_ROWS : totalCount;
  return Math.min(limit ?? ceiling, ceiling);
};

type CustomerReceivable = {
  billedOutstanding: number;
  draftOutstanding: number;
  overdueAmount: number;
  overdueCount: number;
  openInvoiceCount: number;
  settledTotal: number;
  lastSettledOn: string | null;
};

const mkEmptyReceivable = (): CustomerReceivable => ({
  billedOutstanding: 0,
  draftOutstanding: 0,
  overdueAmount: 0,
  overdueCount: 0,
  openInvoiceCount: 0,
  settledTotal: 0,
  lastSettledOn: null,
});

const isUnsentDraft = (invoice: TransactionRecord, settled: number): boolean =>
  !invoice.sentOn && settled <= UNPAID_EPSILON;

const daysPastDue = (dueOn: string | null | undefined, today: string, todayDt: DateTime): number =>
  dueOn && dueOn < today ? Math.floor(todayDt.diff(DateTime.fromISO(dueOn, { zone: 'utc' }), 'days').days) : 0;

const buildReceivablesByCustomer = (state: RootState, today: string): Map<string, CustomerReceivable> => {
  const invoices = getInvoices(state);
  const paidAmounts = selectPaidAmountForInvoicesTypes(state);
  const lastPaidDates = selectInvoicesLastPaidDate(state);

  return invoices.reduce((acc, invoice) => {
    const customerId = invoice.customerId;
    if (!customerId || invoice.isDeleted) return acc;

    const invoiceId = getId(invoice);
    const entry = acc.get(customerId) ?? mkEmptyReceivable();
    const amount = Number.isFinite(invoice.amount) ? invoice.amount : 0;
    const settled = paidAmounts[invoiceId] ?? 0;
    const unpaid = amount - settled;

    entry.settledTotal += settled;

    const lastSettledOn = lastPaidDates[invoiceId];
    if (lastSettledOn && (!entry.lastSettledOn || lastSettledOn > entry.lastSettledOn)) {
      entry.lastSettledOn = lastSettledOn;
    }

    if (unpaid > UNPAID_EPSILON) {
      entry.openInvoiceCount += 1;
      if (isUnsentDraft(invoice, settled)) {
        entry.draftOutstanding += unpaid;
      } else {
        entry.billedOutstanding += unpaid;
        if (invoice.dueOn && invoice.dueOn < today) {
          entry.overdueAmount += unpaid;
          entry.overdueCount += 1;
        }
      }
    }

    acc.set(customerId, entry);
    return acc;
  }, new Map<string, CustomerReceivable>());
};

const round2 = (value: number): number => parseFloat(value.toFixed(2));

const matchesUsage = (txn: TransactionRecord, field: 'businessId' | 'customerId' | 'projectId', value: string) => {
  if (txn[field] === value) return true;
  const items = ensureArray(txn.split?.items ?? []);
  return items.some((item) => item?.[field] === value);
};

type UsageFilters = { businessId: string; customerId?: string; projectId?: string; includeTransfers: boolean };

type UsagePortion = {
  amount: number;
  isSplit: boolean;
  customerId: string | null;
  projectId: string | null;
  coaInfos: CoaInfo[];
};

type UsageLine = {
  amount?: number;
  businessId?: string | null;
  customerId?: string | null;
  projectId?: string | null;
  coa?: TransactionRecord['coa'];
};

type UsageTotals = {
  amount: number;
  matchedLines: number;
  customerIds: Set<string | null>;
  projectIds: Set<string | null>;
  coaInfos: CoaInfo[];
};

const singleValue = (values: Set<string | null>): string | null => (values.size === 1 ? [...values][0] : null);

const resolveUsagePortion = (
  txn: TransactionRecord,
  filters: UsageFilters,
  coaMaps: CoaLookupMaps,
): UsagePortion | null => {
  const items: UsageLine[] = ensureArray(txn.split?.items ?? []);
  const isSplit = items.length > 0;
  const lines: UsageLine[] = isSplit ? items : [txn];

  const totals = lines.reduce<UsageTotals>(
    (acc, line) => {
      if ((line?.businessId ?? null) !== filters.businessId) return acc;
      if (filters.customerId && line?.customerId !== filters.customerId) return acc;
      if (filters.projectId && line?.projectId !== filters.projectId) return acc;
      const coaInfo = getCoaInfoWithMaps(line?.coa, coaMaps);
      if (!filters.includeTransfers && coaInfo.type === 'TRANSFER') return acc;
      acc.amount += line?.amount ?? 0;
      acc.matchedLines += 1;
      acc.customerIds.add(line?.customerId ?? null);
      acc.projectIds.add(line?.projectId ?? null);
      acc.coaInfos.push(coaInfo);
      return acc;
    },
    {
      amount: 0,
      matchedLines: 0,
      customerIds: new Set<string | null>(),
      projectIds: new Set<string | null>(),
      coaInfos: [],
    },
  );

  if (totals.matchedLines === 0) return null;

  return {
    amount: totals.amount,
    isSplit,
    customerId: singleValue(totals.customerIds),
    projectId: singleValue(totals.projectIds),
    coaInfos: totals.coaInfos,
  };
};

const describePortionCoa = (
  portion: UsagePortion,
): { coaType: CoaInfo['type']; coaName: string; splitCategories?: string[] } => {
  if (!portion.isSplit) return { coaType: portion.coaInfos[0].type, coaName: portion.coaInfos[0].name };
  const splitCategories = Array.from(new Set(portion.coaInfos.map((coaInfo) => coaInfo.name)));
  return { coaType: 'SPLIT', coaName: `Split: ${splitCategories.join(', ')}`, splitCategories };
};

const buildUnbilledByProjectId = (state: RootState, customerIds: Set<string>): Map<string, number> => {
  return Array.from(customerIds).reduce((acc, customerId) => {
    const unbilledProjects = projectsSelectors.getUnbilledProjectsByCustomerId(state, customerId);
    unbilledProjects.forEach((row) => {
      if (row?.id) acc.set(row.id, row.unbilled);
    });
    return acc;
  }, new Map<string, number>());
};

export const businessTools = {
  get_businesses: {
    definition: {
      name: 'get_businesses',
      description:
        "Retrieve all businesses on the user's account. Returns business name, type, industry, structure, " +
        'tax group, and accounting type (CASH or ACCRUAL). Call this first when another business tool needs a ' +
        'businessId. NEVER show the id to the user - refer to businesses by name. Be concise and brief.',
      parameters: {
        type: 'object' as const,
        properties: {},
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const ctx = resolveContext();
      if (isContextError(ctx)) return returnToolResponse(msg, { success: false, error: ctx.error });

      const businesses = businessSelectors.getBusinessList(ctx.state);
      const results = businesses.toArray().map((biz) => ({
        id: biz.id,
        name: biz.name,
        nickname: biz.nickname,
        type: biz.type,
        industry: biz.industry,
        structure: biz.structure,
        taxGroup: biz.taxGroup,
        accountingType: biz.accountingType,
        isClosed: biz.isClosed ?? false,
        isDefault: biz.isDefault ?? false,
      }));

      log.debug('get_businesses: returning', results.length, 'businesses');

      return returnToolResponse(msg, {
        success: true,
        count: results.length,
        businesses: results,
        message: `Found ${results.length} business${results.length === 1 ? '' : 'es'}.`,
      });
    },
  },

  get_customers: {
    definition: {
      name: 'get_customers',
      description:
        'Retrieve the customers (clients) on the account with their receivables position. For each customer ' +
        'returns totalOutstanding (billed + draft), billedOutstanding (unpaid on invoices that were sent or have a ' +
        'payment applied), draftOutstanding (unsent invoices with nothing applied), overdueAmount, overdueCount, ' +
        'openInvoiceCount, settledTotal (payments plus credits and write-offs applied to invoices) and ' +
        'lastSettledOn. settledTotal is not the same as cash received. ' +
        'Use this for "who owes me the most", "which customers are overdue" and similar receivables questions. ' +
        'For "who owes me the most", rank by billedOutstanding, not totalOutstanding, and mention drafts ' +
        'separately so a large unsent invoice does not put the wrong client first. ' +
        'Archived customers are included only while they still have unpaid invoices. ' +
        'businessId is the business the customer belongs to; a customer with no business saved belongs to the ' +
        'default business. ' +
        'An unsent invoice counts as billed (and can be overdue) as soon as any payment or credit is applied, ' +
        'which can differ from get_invoice_dashboard_summary. ' +
        'Amounts are in the account currency. NEVER show ids to the user. Be concise and brief.',
      parameters: {
        type: 'object' as const,
        properties: {},
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const ctx = resolveContext();
      if (isContextError(ctx)) return returnToolResponse(msg, { success: false, error: ctx.error });
      const { state, today } = ctx;

      const customers = customersSelectors.getCustomersWithArchived(state);
      if (!customers) {
        return returnToolResponse(msg, { success: true, count: 0, customers: [], message: 'No customers found.' });
      }

      const receivablesByCustomer = buildReceivablesByCustomer(state, today);
      const defaultBusinessId = businessSelectors.getDefaultBusiness(state)?.id;

      const results = customers.valueSeq().reduce<object[]>((acc, customer) => {
        if (customer.isGuest || customer.isDeleted) return acc;
        const customerId = customer.id ?? '';
        const receivable = receivablesByCustomer.get(customerId) ?? mkEmptyReceivable();
        if (customer.isArchived && receivable.openInvoiceCount === 0) return acc;

        acc.push({
          id: customerId,
          name: customersSelectors.getCustomerNameById(state, customerId),
          businessId: resolveBusinessId(customer.businessId, defaultBusinessId),
          isArchived: customer.isArchived ?? false,
          totalOutstanding: round2(receivable.billedOutstanding + receivable.draftOutstanding),
          billedOutstanding: round2(receivable.billedOutstanding),
          draftOutstanding: round2(receivable.draftOutstanding),
          overdueAmount: round2(receivable.overdueAmount),
          overdueCount: receivable.overdueCount,
          openInvoiceCount: receivable.openInvoiceCount,
          settledTotal: round2(receivable.settledTotal),
          lastSettledOn: receivable.lastSettledOn,
        });
        return acc;
      }, []);

      log.debug('get_customers: returning', results.length, 'customers');

      return returnToolResponse(msg, {
        success: true,
        count: results.length,
        customers: results,
        message: `Found ${results.length} customer${results.length === 1 ? '' : 's'}.`,
      });
    },
  },

  get_projects: {
    definition: {
      name: 'get_projects',
      description:
        'Retrieve projects with their status, due date and unbilled amount. Status is one of NOT_STARTED, ' +
        'IN_PROGRESS, COMPLETED, PAST_DUE. Use this for "what projects are pending", "which projects are overdue" ' +
        'and "how much unbilled work do I have". isOverdue is true when the due date has passed and the project is ' +
        "not complete. unbilledAmount is only populated for active projects. businessId is the project's business, " +
        "falling back to its client's business and then the default business when none is saved. NEVER show ids " +
        'to the user. ' +
        'Be concise and brief.',
      parameters: {
        type: 'object' as const,
        properties: {
          businessId: {
            type: 'string' as const,
            description: 'Only return projects for this business (from get_businesses).',
          },
          customerId: {
            type: 'string' as const,
            description: 'Only return projects for this customer (from get_customers).',
          },
          status: {
            type: 'string' as const,
            enum: ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'PAST_DUE'],
            description: 'Only return projects in this status. Omit to return every project.',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const ctx = resolveContext();
      if (isContextError(ctx)) return returnToolResponse(msg, { success: false, error: ctx.error });
      const { state, today } = ctx;

      const validation = getProjectsArgsSchema.safeParse(safeParse(msg.arguments) ?? {});
      if (!validation.success) {
        return returnToolResponse(msg, {
          success: false,
          error: buildValidationError('get_projects', validation.error.issues),
        });
      }
      const { businessId, customerId, status } = validation.data;

      const projects = projectsSelectors.getProjects(state);
      const customers = customersSelectors.getCustomersWithArchived(state);
      const defaultBusinessId = businessSelectors.getDefaultBusiness(state)?.id;

      const matched = projects.valueSeq().reduce<MatchedProject[]>((acc, project) => {
        if (project.isDeleted) return acc;
        if (customerId && project.customerId !== customerId) return acc;
        if (status && project.status !== status) return acc;
        const projectBusinessId = resolveBusinessId(
          project.businessId,
          project.customerId ? customers?.get(project.customerId)?.businessId : undefined,
          defaultBusinessId,
        );
        if (businessId && projectBusinessId !== businessId) return acc;
        acc.push({ project, projectBusinessId });
        return acc;
      }, []);

      const customerIds = matched.reduce<Set<string>>((acc, { project }) => {
        if (project.customerId) acc.add(project.customerId);
        return acc;
      }, new Set<string>());
      const unbilledByProjectId = buildUnbilledByProjectId(state, customerIds);

      const results = matched.map(({ project, projectBusinessId }) => {
        const projectStatus = project.status as ProjectStatus | undefined;
        return {
          id: project.id,
          name: project.name,
          status: projectStatus,
          dueOn: project.dueOn ?? null,
          completedOn: project.completedOn ?? null,
          isOverdue: !!project.dueOn && project.dueOn < today && projectStatus !== 'COMPLETED',
          customerId: project.customerId ?? null,
          customerName: project.customerId ? customersSelectors.getCustomerNameById(state, project.customerId) : null,
          businessId: projectBusinessId,
          unbilledAmount: round2(unbilledByProjectId.get(project.id ?? '') ?? 0),
        };
      });

      results.sort((a, b) => (a.dueOn ?? '9999-12-31').localeCompare(b.dueOn ?? '9999-12-31'));

      log.debug('get_projects: returning', results.length, 'projects');

      return returnToolResponse(msg, {
        success: true,
        count: results.length,
        projects: results,
        message: `Found ${results.length} project${results.length === 1 ? '' : 's'}.`,
      });
    },
  },

  get_invoices: {
    definition: {
      name: 'get_invoices',
      description:
        'Retrieve individual invoices with their computed status and balance. status is one of draft, sent, ' +
        'partial, overdue, paid. Each row returns amount (invoice total), settledAmount (payments plus credits ' +
        'applied), unpaidAmount, dueOn, sentOn, lastSettledOn and daysOverdue (0 unless unpaid and past due). Use this for "show me my overdue invoices", "what is due this week", ' +
        'or invoices for one customer or project. For an account-wide total use get_invoice_dashboard_summary ' +
        'instead. When limitReached is true, the returned rows are incomplete; do not present their sum as the ' +
        'full matching total. NEVER show ids to the user. Be concise and brief.',
      parameters: {
        type: 'object' as const,
        properties: {
          businessId: { type: 'string' as const, description: 'Only invoices for this business.' },
          customerId: { type: 'string' as const, description: 'Only invoices for this customer.' },
          projectId: { type: 'string' as const, description: 'Only invoices for this project.' },
          status: {
            type: 'string' as const,
            enum: ['draft', 'sent', 'partial', 'overdue', 'paid'],
            description: 'Only invoices in this status.',
          },
          dueAfter: {
            type: 'string' as const,
            description: 'Only invoices due on or after this date, YYYY-MM-DD.',
          },
          dueBefore: {
            type: 'string' as const,
            description: 'Only invoices due on or before this date, YYYY-MM-DD.',
          },
          limit: { type: 'integer' as const, description: 'Maximum number of invoices to return.' },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const ctx = resolveContext();
      if (isContextError(ctx)) return returnToolResponse(msg, { success: false, error: ctx.error });
      const { state, today } = ctx;

      const validation = getInvoicesArgsSchema.safeParse(safeParse(msg.arguments) ?? {});
      if (!validation.success) {
        return returnToolResponse(msg, {
          success: false,
          error: buildValidationError('get_invoices', validation.error.issues),
        });
      }
      const { businessId, customerId, projectId, status, dueAfter, dueBefore, limit } = validation.data;

      const invoices = getInvoices(state);
      const paidAmounts = selectPaidAmountForInvoicesTypes(state);
      const statuses = selectInvoiceChipStatuses(state);
      const lastPaidDates = selectInvoicesLastPaidDate(state);
      const todayDt = DateTime.fromISO(today, { zone: 'utc' });

      const matched = invoices.reduce<Array<Record<string, unknown>>>((acc, invoice) => {
        if (invoice.isDeleted) return acc;
        if (businessId && !matchesUsage(invoice, 'businessId', businessId)) return acc;
        if (customerId && invoice.customerId !== customerId) return acc;
        if (projectId && !matchesUsage(invoice, 'projectId', projectId)) return acc;
        if (dueAfter && (!invoice.dueOn || invoice.dueOn < dueAfter)) return acc;
        if (dueBefore && (!invoice.dueOn || invoice.dueOn > dueBefore)) return acc;

        const invoiceId = getId(invoice);
        const invoiceStatus = statuses[invoiceId];
        if (status && invoiceStatus !== status) return acc;

        const amount = Number.isFinite(invoice.amount) ? invoice.amount : 0;
        const settled = paidAmounts[invoiceId] ?? 0;
        const isOpen = amount - settled > UNPAID_EPSILON && invoiceStatus !== 'draft';
        const daysOverdue = isOpen ? daysPastDue(invoice.dueOn, today, todayDt) : 0;

        acc.push({
          id: invoiceId,
          invoiceNo: invoice.invoiceNo,
          status: invoiceStatus,
          customerId: invoice.customerId ?? null,
          projectId: invoice.projectId ?? null,
          businessId: invoice.businessId ?? null,
          amount: round2(amount),
          settledAmount: round2(settled),
          unpaidAmount: round2(amount - settled),
          postedOn: invoice.postedOn,
          sentOn: invoice.sentOn ?? null,
          dueOn: invoice.dueOn ?? null,
          lastSettledOn: lastPaidDates[invoiceId] ?? null,
          daysOverdue,
        });
        return acc;
      }, []);

      matched.sort((a, b) => String(a.dueOn ?? '9999-12-31').localeCompare(String(b.dueOn ?? '9999-12-31')));

      const maxRows = resolveMaxRows(state, limit, matched.length);
      const limitReached = matched.length > maxRows;
      const results = matched.slice(0, maxRows).map((row) => ({
        ...row,
        customerName:
          typeof row.customerId === 'string' ? customersSelectors.getCustomerNameById(state, row.customerId) : null,
      }));

      log.debug('get_invoices: returning', results.length, 'of', matched.length);

      return returnToolResponse(msg, {
        success: true,
        count: results.length,
        totalMatches: matched.length,
        limitReached,
        invoices: results,
        message: limitReached
          ? `Found ${matched.length} invoices. Returning the ${results.length} due soonest, so these rows are incomplete and their sum is not the total. The user can narrow the search by customer, status or date.`
          : `Found ${matched.length} invoice${matched.length === 1 ? '' : 's'}.`,
      });
    },
  },

  get_accounts_receivable_aging: {
    definition: {
      name: 'get_accounts_receivable_aging',
      description:
        'Get an accounts receivable aging breakdown of every unpaid billed invoice, bucketed by how long it has ' +
        'been overdue: current (not yet due), days1to30, days31to60, days61to90 and over90. Each bucket returns ' +
        'amount and count. Use this for "how old is my receivables balance" or "how much is more than 60 days ' +
        'late". Drafts (unsent invoices with nothing applied) are excluded. Be concise and brief.',
      parameters: {
        type: 'object' as const,
        properties: {
          customerId: {
            type: 'string' as const,
            description: 'Restrict the aging report to a single customer.',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const ctx = resolveContext();
      if (isContextError(ctx)) return returnToolResponse(msg, { success: false, error: ctx.error });
      const { state, today } = ctx;

      const validation = getAgingArgsSchema.safeParse(safeParse(msg.arguments) ?? {});
      if (!validation.success) {
        return returnToolResponse(msg, {
          success: false,
          error: buildValidationError('get_accounts_receivable_aging', validation.error.issues),
        });
      }
      const { customerId } = validation.data;

      const invoices = getInvoices(state);
      const paidAmounts = selectPaidAmountForInvoicesTypes(state);
      const todayDt = DateTime.fromISO(today, { zone: 'utc' });

      const buckets = {
        current: { amount: 0, count: 0 },
        days1to30: { amount: 0, count: 0 },
        days31to60: { amount: 0, count: 0 },
        days61to90: { amount: 0, count: 0 },
        over90: { amount: 0, count: 0 },
      };

      let totalOutstanding = 0;

      for (const invoice of invoices) {
        if (invoice.isDeleted) continue;
        if (customerId && invoice.customerId !== customerId) continue;

        const amount = Number.isFinite(invoice.amount) ? invoice.amount : 0;
        const settled = paidAmounts[getId(invoice)] ?? 0;
        const unpaid = amount - settled;
        if (unpaid <= UNPAID_EPSILON || isUnsentDraft(invoice, settled)) continue;

        totalOutstanding += unpaid;

        const daysOverdue = daysPastDue(invoice.dueOn, today, todayDt);

        const bucket =
          daysOverdue <= 0
            ? buckets.current
            : daysOverdue <= 30
              ? buckets.days1to30
              : daysOverdue <= 60
                ? buckets.days31to60
                : daysOverdue <= 90
                  ? buckets.days61to90
                  : buckets.over90;

        bucket.amount += unpaid;
        bucket.count += 1;
      }

      const rounded = Object.entries(buckets).reduce<Record<string, { amount: number; count: number }>>(
        (acc, [key, bucket]) => {
          acc[key] = { amount: round2(bucket.amount), count: bucket.count };
          return acc;
        },
        {},
      );

      log.debug('get_accounts_receivable_aging: total', totalOutstanding);

      return returnToolResponse(msg, {
        success: true,
        totalOutstanding: round2(totalOutstanding),
        buckets: rounded,
        message: 'Aging buckets cover unpaid billed invoices only; drafts are excluded.',
      });
    },
  },

  get_invoice_dashboard_summary: {
    definition: {
      name: 'get_invoice_dashboard_summary',
      description:
        "Get an account-wide summary of the user's invoicing position: overdue amount and count, amount due in " +
        'the next 7 days, draft invoices, and invoice payments received in the last 90 days ' +
        '(paymentsReceivedLast90Days counts each payment by the date it was received; credit memos are excluded). Use this for a high-level ' +
        '"how is my business doing" or "what do I need to chase" answer. For individual invoices call ' +
        'get_invoices instead. This summary follows the Invoices page, which counts an unsent invoice as a draft ' +
        'until it is fully paid; get_customers and get_accounts_receivable_aging count it as billed once any ' +
        'payment is applied, so their overdue totals can differ. Amounts are in the account currency. ' +
        'Be concise and brief.',
      parameters: {
        type: 'object' as const,
        properties: {},
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const ctx = resolveContext();
      if (isContextError(ctx)) return returnToolResponse(msg, { success: false, error: ctx.error });

      const { paidLast90Days: _lifetimeSettled, ...summary } = selectInvoiceDashboardSummary(ctx.state);
      const ninetyDaysAgo = DateTime.fromISO(ctx.today).minus({ days: 90 }).toISODate() ?? ctx.today;

      log.debug('get_invoice_dashboard_summary');

      const roundedSummary = Object.entries({
        ...summary,
        paymentsReceivedLast90Days: sumPaymentsReceivedSince(ctx.state, ninetyDaysAgo),
      }).reduce<Record<string, number>>((acc, [key, value]) => {
        acc[key] = round2(value);
        return acc;
      }, {});

      return returnToolResponse(msg, {
        success: true,
        ...roundedSummary,
        message: 'Summary covers all businesses on the account.',
      });
    },
  },

  get_top_products_and_services: {
    definition: {
      name: 'get_top_products_and_services',
      description:
        'Get the products and services with the highest invoiced amounts on sent invoices that had a payment or ' +
        'credit applied in the last 90 days. Returns name, itemType, invoiceCount, totalUnits and totalAmount per ' +
        'item, sorted by totalAmount. totalAmount is the full invoiced line amount, even when the invoice is only ' +
        'partly settled, so it is not revenue or cash received; say "invoiced" rather than "earned". Use this for ' +
        '"what is selling best". NEVER show ids to the user. Be concise and brief.',
      parameters: {
        type: 'object' as const,
        properties: {
          limit: { type: 'integer' as const, description: 'Maximum number of items to return.' },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const ctx = resolveContext();
      if (isContextError(ctx)) return returnToolResponse(msg, { success: false, error: ctx.error });
      const { state } = ctx;

      const validation = getTopProductsArgsSchema.safeParse(safeParse(msg.arguments) ?? {});
      if (!validation.success) {
        return returnToolResponse(msg, {
          success: false,
          error: buildValidationError('get_top_products_and_services', validation.error.issues),
        });
      }
      const { limit } = validation.data;

      const rows = selectProductsAndServicesStatsLast90Days(state);
      const sorted = [...rows].sort((a, b) => b.totalAmount - a.totalAmount);

      const maxRows = resolveMaxRows(state, limit, sorted.length);
      const limitReached = sorted.length > maxRows;
      const results = sorted.slice(0, maxRows).map((row) => ({
        name: row.name,
        itemType: row.itemType,
        isArchived: row.isArchived,
        invoiceCount: row.invoiceCount,
        totalUnits: row.totalUnits,
        totalAmount: round2(row.totalAmount),
      }));

      log.debug('get_top_products_and_services: returning', results.length, 'items');

      return returnToolResponse(msg, {
        success: true,
        count: results.length,
        totalMatches: sorted.length,
        limitReached,
        items: results,
        message: `Top products and services by invoiced amount on invoices settled in the last 90 days (${results.length} shown). Amounts are invoiced, not cash received.`,
      });
    },
  },

  get_business_transactions: {
    definition: {
      name: 'get_business_transactions',
      description:
        'Retrieve transactions tagged to a business, with optional date, customer and project filters. Returns ' +
        'payee, amount, posted date, category (coaType, coaName, and splitCategories for splits) and linked ' +
        'customer/project. Positive amounts are money in, negative amounts are money out; use coaType and coaName ' +
        'to tell income and expenses apart. For a split transaction, amount is only the portion assigned to the ' +
        'business (and to the customer/project if filtered) and totalAmount is the whole transaction. Transfers ' +
        'between accounts are excluded unless includeTransfers is true, because they are neither income nor ' +
        'expense. Invoices, credit memos and other Accounts Receivable records are excluded. Use get_invoices for ' +
        'invoices; credit memo and invoice payment details are not available through these tools. Investment, estimate and unaccepted scheduled transactions are also excluded. Defaults to the last ' +
        '90 days when no dates are given. When limitReached is true, the returned rows are incomplete; do not present ' +
        'their sum as the full matching total. NEVER show ids to the user. Be concise and brief.',
      parameters: {
        type: 'object' as const,
        properties: {
          businessId: {
            type: 'string' as const,
            description: 'The business to retrieve transactions for (from get_businesses).',
          },
          startDate: {
            type: 'string' as const,
            description: 'Earliest posted date to include, YYYY-MM-DD. Defaults to 90 days ago.',
          },
          endDate: {
            type: 'string' as const,
            description: 'Latest posted date to include, YYYY-MM-DD. Defaults to today.',
          },
          customerId: { type: 'string' as const, description: 'Only transactions for this customer.' },
          projectId: { type: 'string' as const, description: 'Only transactions for this project.' },
          includeTransfers: {
            type: 'boolean' as const,
            description: 'Include transfers between accounts. Defaults to false.',
          },
          limit: { type: 'integer' as const, description: 'Maximum number of transactions to return.' },
        },
        required: ['businessId'],
      },
    },
    handler: (msg: ToolMessage) => {
      const ctx = resolveContext();
      if (isContextError(ctx)) return returnToolResponse(msg, { success: false, error: ctx.error });
      const { state, today } = ctx;

      const validation = getBusinessTransactionsArgsSchema.safeParse(safeParse(msg.arguments) ?? {});
      if (!validation.success) {
        return returnToolResponse(msg, {
          success: false,
          error: buildValidationError('get_business_transactions', validation.error.issues),
        });
      }
      const { businessId, startDate, endDate, customerId, projectId, includeTransfers, limit } = validation.data;

      const from = startDate ?? DateTime.fromISO(today, { zone: 'utc' }).minus({ days: 90 }).toISODate()!;
      const to = endDate ?? today;

      const txnsById = getResourcesById(state);
      const arAccountIds = buildArAccountIds(state);
      const coaMaps: CoaLookupMaps = {
        categoriesById: getCategoriesById(state),
        accountsById: accountsSelectors.getAccountsById(state),
      };

      const matched = txnsById.valueSeq().reduce<Array<Record<string, unknown>>>((acc, txn) => {
        if (txn.isDeleted || isInvestmentTransaction(txn) || isEstimatedTransaction(txn)) return acc;
        if (isUnacceptedScheduledTxn(txn)) return acc;
        if (txn.accountId && arAccountIds.has(txn.accountId)) return acc;
        if (!txn.postedOn || txn.postedOn < from || txn.postedOn > to) return acc;

        const portion = resolveUsagePortion(
          txn,
          { businessId, customerId, projectId, includeTransfers: includeTransfers ?? false },
          coaMaps,
        );
        if (!portion) return acc;

        acc.push({
          id: getId(txn),
          payee: txn.payee,
          amount: round2(portion.amount),
          ...(portion.isSplit ? { totalAmount: round2(txn.amount) } : {}),
          ...describePortionCoa(portion),
          postedOn: txn.postedOn,
          accountId: txn.accountId,
          customerId: portion.customerId,
          projectId: portion.projectId,
          memo: txn.memo ?? null,
        });
        return acc;
      }, []);

      matched.sort((a, b) => String(b.postedOn).localeCompare(String(a.postedOn)));

      const maxRows = resolveMaxRows(state, limit, matched.length);
      const limitReached = matched.length > maxRows;
      const results = matched.slice(0, maxRows);

      log.debug('get_business_transactions: returning', results.length, 'of', matched.length);

      return returnToolResponse(msg, {
        success: true,
        count: results.length,
        totalMatches: matched.length,
        limitReached,
        dateRange: { from, to },
        transactions: results,
        message: limitReached
          ? `Found ${matched.length} transactions between ${from} and ${to}. Returning the ${results.length} most recent, so these rows are incomplete and their sum is not the total. The user can narrow the date range if needed.`
          : `Found ${matched.length} transaction${matched.length === 1 ? '' : 's'} between ${from} and ${to}.`,
      });
    },
  },
};
